/**
 * Where the seconds of one voice turn go, in development.
 *
 * A turn on the call is six hops: the quiet that ends it, the recorder's stop,
 * the transcript coming back, the send, the first part of the agent's answer
 * landing, and the first sound of the reply. The plan that split onboarding
 * into two phases (docs/plans/done/onboarding-two-phases.md, decision 8) promised
 * the win would be measured, not guessed; this is the phone's half of that
 * measurement. The server logs its half (`stt_ms`, `model_ms`, `tts_ms`).
 *
 * One line per turn, when the reply starts playing, in the console only, and
 * only in a development build. Pure apart from the clock and the sink, so the
 * line can be proven from marks fed by hand.
 */

/**
 * The six hops of a turn on the four-hop call, and the three of a turn on the
 * live one: the provider session coming up (`open`, once a call), the model's
 * first audio frame (`speaking`) and the finished turn with its note (`turn`).
 */
export type TurnStage = 'quiet' | 'stopped' | 'heard' | 'sent' | 'first_part' | 'spoken' | 'open' | 'speaking' | 'turn';

const STAGES: readonly TurnStage[] = ['open', 'quiet', 'stopped', 'heard', 'sent', 'first_part', 'speaking', 'spoken', 'turn'];

/** A stage that begins a turn, once the last one has been logged. */
const OPENS: readonly TurnStage[] = ['quiet', 'sent', 'open', 'speaking'];

/** A stage that ends one: the line is logged and the next opener starts afresh. */
const CLOSES: readonly TurnStage[] = ['spoken', 'turn'];

export type TurnTimeline = {
  /** A stage was reached. The first `quiet` after a `spoken` starts a new turn; a stage marked twice keeps its first time. */
  readonly mark: (stage: TurnStage) => void;
  /** The line the last complete turn would log, or null while a turn is open. */
  readonly line: () => string | null;
};

/** The gaps between the stages that were reached, as `a→b 1234ms`, then the whole. */
export const timelineLine = (marks: Readonly<Partial<Record<TurnStage, number>>>): string | null => {
  const reached = STAGES.filter((stage) => marks[stage] !== undefined);
  if (reached.length < 2) return null;
  const parts: string[] = [];
  for (let at = 1; at < reached.length; at += 1) {
    const from = reached[at - 1] as TurnStage;
    const to = reached[at] as TurnStage;
    parts.push(`${from}→${to} ${Math.round((marks[to] as number) - (marks[from] as number))}ms`);
  }
  const first = reached[0] as TurnStage;
  const last = reached[reached.length - 1] as TurnStage;
  parts.push(`total ${Math.round((marks[last] as number) - (marks[first] as number))}ms`);
  return parts.join(' · ');
};

export const createTurnTimeline = (
  sink: (line: string) => void,
  now: () => number = () => Date.now(),
): TurnTimeline => {
  let marks: Partial<Record<TurnStage, number>> = {};
  let closed = false;
  return {
    mark: (stage) => {
      // A new turn begins at its first stage once the last one was logged. A
      // typed turn has no quiet and no stop: it begins at `sent`. A live turn
      // begins where the model does, at its first audio frame.
      if (closed && OPENS.includes(stage)) {
        marks = {};
        closed = false;
      }
      if (marks[stage] !== undefined) return;
      marks[stage] = now();
      if (CLOSES.includes(stage)) {
        closed = true;
        const line = timelineLine(marks);
        if (line !== null) sink(`voice turn: ${line}`);
      }
    },
    line: () => (closed ? timelineLine(marks) : null),
  };
};

/**
 * The one timeline the call uses. Nothing in a release build: the sink is a
 * no-op there, and the marks cost nothing worth measuring. `bun test` has no
 * `__DEV__` at all, so it is read by name and not assumed.
 */
export const turnTimeline: TurnTimeline = createTurnTimeline((line) => {
  if (typeof __DEV__ !== 'undefined' && __DEV__) console.log(line);
});
